/**
 * VERONI progressive catalogue loading.
 * Automatic loading is an enhancement: the real next-page link stays usable.
 */
(function () {
  if (window.__veroniInfiniteScroll) return;
  window.__veroniInfiniteScroll = true;

  if (!document.getElementById('v-infinite-css')) {
    const style = document.createElement('style');
    style.id = 'v-infinite-css';
    style.textContent =
      '.v-infinite-sentinel{width:100%;height:1px;overflow-anchor:none}' +
      '.v-grid-note{overflow-anchor:none}' +
      '.v-infinite-fallback[hidden]{display:none!important}' +
      '.v-infinite-fallback[aria-busy="true"]{opacity:.65;cursor:progress}' +
      '.v-infinite-status{margin:0;max-width:48ch}' +
      '.is-infinite-in{animation:v-infinite-fade .3s ease both}' +
      '@keyframes v-infinite-fade{from{opacity:0}to{opacity:1}}' +
      '@media(prefers-reduced-motion:reduce){.is-infinite-in{animation:none}}';
    document.head.appendChild(style);
  }

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function productKey(el) {
    // The inline helper may contain recommendation links, but is not a product.
    if (el.matches && el.matches('[data-finder-inline]')) return '';
    if (el.dataset && el.dataset.productId) return 'id:' + el.dataset.productId;
    const link = el.matches && el.matches('a[href]') ? el : el.querySelector && el.querySelector('a[href*="/products/"]');
    if (!link) return '';
    const path = (link.getAttribute('href') || '').split('?')[0].replace(/\/+$/, '');
    return path ? 'url:' + path : '';
  }

  function existingKeys(grid) {
    return new Set(Array.from(grid.children).map(productKey).filter(Boolean));
  }

  function bindRegion(region) {
    if (region.dataset.veroniPaginationBound) return;
    const grid = region.querySelector('[data-veroni-grid]');
    const more = region.querySelector('[data-veroni-load-more]');
    const count = region.querySelector('[data-veroni-grid-count]');
    if (!grid || !more || !more.getAttribute('href')) return;

    function pageUrl(href) {
      if (!href) return '';
      const url = new URL(href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
        throw new Error('Unexpected pagination destination');
      }
      url.hash = '';
      return url.href;
    }

    let nextUrl;
    try { nextUrl = pageUrl(more.getAttribute('href')); } catch (_) { return; }
    region.dataset.veroniPaginationBound = 'true';
    const completed = new Set([pageUrl(window.location.href)]);
    let busy = false;
    let automaticPaused = false;
    let observer;
    let scrollQueued = false;
    const originalLabel = more.textContent.trim() || 'Load more';
    const countMatch = (count ? count.textContent : '').match(/Showing\s+([\d,]+)\s*[–-]\s*([\d,]+)\s+of\s+([\d,]+)/i);
    const first = countMatch ? Number(countMatch[1].replace(/,/g, '')) : 1;
    let total = countMatch ? Number(countMatch[3].replace(/,/g, '')) : Number(count && count.getAttribute('data-total')) || 0;

    more.hidden = false;
    more.removeAttribute('aria-hidden');
    more.removeAttribute('tabindex');
    const note = region.querySelector('.v-grid-note') || region;
    const status = document.createElement('p');
    status.className = 'v-micro v-infinite-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    const fallback = document.createElement('a');
    fallback.className = 'v-micro';
    fallback.textContent = 'Open the next page instead';
    fallback.href = nextUrl;
    fallback.hidden = true;
    const sentinel = document.createElement('div');
    sentinel.className = 'v-infinite-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    note.appendChild(status);
    note.appendChild(fallback);
    note.appendChild(sentinel);

    function setBusy(on) {
      busy = on;
      more.setAttribute('aria-busy', String(on));
      region.setAttribute('aria-busy', String(on));
      more.textContent = on ? 'Loading more…' : originalLabel;
    }

    function updateCount() {
      if (!count) return;
      // Finder/search listeners apply their matching-result count after our event.
      if (document.querySelector('[data-veroni-finder].has-results, [data-veroni-search-filters].has-filters')) return;
      const shown = existingKeys(grid).size;
      if (countMatch) count.textContent = 'Showing ' + first + '–' + (first + shown - 1) + ' of ' + total;
      else if (/exact match/i.test(count.textContent)) count.textContent = shown + (shown === 1 ? ' exact match' : ' exact matches');
    }

    function parseNext(html, requestedUrl) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming = doc.querySelector('[data-veroni-pagination-region] [data-veroni-grid]') || doc.querySelector('[data-veroni-grid]');
      // Connection-challenge or error HTML must never be mistaken for the last page.
      if (!incoming) throw new Error('The next product grid is missing');
      const incomingMore = doc.querySelector('[data-veroni-pagination-region] [data-veroni-load-more]') || doc.querySelector('[data-veroni-load-more]');
      const next = pageUrl(incomingMore && incomingMore.getAttribute('href'));
      if (next && (next === requestedUrl || completed.has(next))) throw new Error('Pagination did not advance');
      const incomingCount = doc.querySelector('[data-veroni-grid-count]');
      const match = (incomingCount ? incomingCount.textContent : '').match(/\bof\s+([\d,]+)/i);
      return {nodes: Array.from(incoming.children), next: next, total: match ? Number(match[1].replace(/,/g, '')) : total};
    }

    function appendBatch(batch, manual) {
      const have = existingKeys(grid);
      const fragment = document.createDocumentFragment();
      let added = 0;
      let firstNew;
      batch.nodes.forEach(function (node) {
        if (node.matches && node.matches('[data-finder-editorial], [data-finder-inline], script')) return;
        const key = productKey(node);
        if (!key || have.has(key)) return;
        have.add(key);
        const el = document.importNode(node, true);
        el.classList.add('is-infinite-in');
        el.style.scrollMarginTop = 'calc(var(--header-height, 68px) + 96px)';
        el.querySelectorAll('img').forEach(function (img) {
          img.loading = 'lazy';
          img.removeAttribute('fetchpriority');
        });
        fragment.appendChild(el);
        if (!firstNew) firstNew = el;
        added++;
      });
      grid.appendChild(fragment);
      total = batch.total;
      updateCount();
      document.dispatchEvent(new CustomEvent('veroni:grid-appended', {detail: {added: added}}));
      if (manual && firstNew) {
        const link = firstNew.matches('a[href]') ? firstNew : firstNew.querySelector('a[href]');
        if (link) link.focus({preventScroll: true});
        firstNew.scrollIntoView({block: 'start', behavior: reduceMotion ? 'auto' : 'smooth'});
      }
      return added;
    }

    async function loadMore(manual) {
      if (busy || !nextUrl || (!manual && automaticPaused)) return;
      automaticPaused = false;
      const requestedUrl = nextUrl;
      if (completed.has(requestedUrl)) return;
      setBusy(true);
      status.textContent = 'Loading more products…';
      fallback.hidden = true;
      const controller = new AbortController();
      const timeout = setTimeout(function () { controller.abort(); }, 20000);
      try {
        const response = await fetch(requestedUrl, {credentials: 'same-origin', signal: controller.signal});
        if (!response.ok) throw new Error('Page ' + response.status);
        const batch = parseNext(await response.text(), requestedUrl);
        const added = appendBatch(batch, manual);
        completed.add(requestedUrl);
        nextUrl = batch.next;
        if (nextUrl) {
          more.href = nextUrl;
          fallback.href = nextUrl;
          status.textContent = added + ' more products loaded.';
        } else {
          more.remove();
          fallback.remove();
          sentinel.remove();
          if (observer) observer.disconnect();
          window.removeEventListener('scroll', onScroll);
          status.textContent = first === 1 ? 'All ' + existingKeys(grid).size + ' products loaded.' : 'You’ve reached the end.';
        }
      } catch (_) {
        automaticPaused = true; // No automatic request loop on a failed/challenged page.
        status.textContent = 'More products are available. Loading paused—please try again or open the next page.';
        fallback.href = requestedUrl;
        fallback.hidden = false;
      } finally {
        clearTimeout(timeout);
        setBusy(false);
      }
      // Filtering or a short batch may leave the sentinel inside the viewport.
      if (nextUrl && !automaticPaused) window.requestAnimationFrame(maybeAutoLoad);
    }

    function maybeAutoLoad() {
      if (busy || automaticPaused || !nextUrl || !region.isConnected) return;
      if (sentinel.getBoundingClientRect().top <= window.innerHeight + 600) loadMore(false);
    }

    function onScroll() {
      if (scrollQueued) return;
      scrollQueued = true;
      window.requestAnimationFrame(function () {
        scrollQueued = false;
        maybeAutoLoad();
      });
    }

    more.addEventListener('click', function (event) {
      // Preserve open-in-new-tab and the plain next-page URL without JavaScript.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      return loadMore(true);
    });

    // A missing observer must never remove the manual way forward.
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) maybeAutoLoad();
      }, {rootMargin: '600px 0px'});
      observer.observe(sentinel);
    }
    window.addEventListener('scroll', onScroll, {passive: true});
    document.addEventListener('veroni:search-filter', maybeAutoLoad);
  }

  function start() {
    document.querySelectorAll('[data-veroni-pagination-region]').forEach(bindRegion);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', start);
})();
